// LayerPopup — W3 Classic レイヤー追加 (consult 2026-05-26, docs/_wip/w3-classic-layers.md).
//
// 旧 Java 版の 3 層を Classic UI に逆輸入する常駐 floating popup.
// 「旧 Java 版のレイヤー機能、新版に復活」の SNS 弾 (launch-rollout-plan.md §3 W3).
//
// ## 設計の核心 (確定事項、§2.2)
// - **出しっぱなし**: 閉じるボタンなし / 開閉トグルなし (= 発見性問題が消滅、
//   状態とコードが最小). ChatPanel detach 小窓のような × は付けない.
// - **ドラッグ移動だけ可能**: ヘッダ (タイトルバー) を掴んで動かす. 閉じる ≠
//   動かす. 位置は per-user に localStorage 記憶 (nice-to-have).
// - **Tab UI hide に連動**: caller (ClassicLayout) が `uiHidden` トグル対象の
//   chrome 内に本 popup をレンダリングするため、Tab で他 UI ごと消える. 本
//   component 内に専用の隠すボタンは持たない.
// - **3 行リスト**: 上から id2 → id1 → id0 (= リスト上端が最前面、Photoshop 慣習).
//   ラベルは作成順ネーミング (id+1) で上から レイヤー3 / レイヤー2 / レイヤー1 (§6).
//   各行 `[👁 表示トグル] [ラベル レイヤーN] [● アクティブ選択]` の **2 操作だけ**.
//   内部 id は 0/1/2 据え置き、表示ラベルのみ レイヤーN (= id+1).
//
// ## per-layer clear (ゴミ箱ボタン) を外した経緯 (2026-05-26)
// ゴミ箱アイコンが「レイヤー削除」と混同される + per-layer clear は破壊的で頻度が
// 低いため、W3 popup からは UI 露出を外した (= 各行は選択 + 👁 表示の 2 操作に純化).
// 全消し (ClassicLayout 既存 ConfirmDialog 経由の引数なし full clear) はそのまま存続.
// 個別層 clear の能力自体は W5 admin 管理 UI で再利用するため protocol / server /
// useCases に温存 (= popup の配線だけ撤去).
//
// ## 操作 2 つの配線先 (UI からエンジン内部を直接いじらない)
// 1. アクティブ層選択 → `onSelectActive(id)` → ClassicLayout が `PenConfig.layerId`
//    を更新 (= 以降の stroke がその層に乗る). デフォルトは最前面 (id2 = レイヤー3).
// 2. 👁 表示トグル → `onToggleVisible(id, visible)` → `engine.setLayerVisible`
//    (= クライアントローカル / broadcast しない, §2.3). 表示状態は本 popup の
//    local state で保持.
//
// ## 入れない (= Modern / Phase 2++ の領分, §2.2)
// 不透明度 / ブレンドモード / 並べ替え / サムネ拡充. スマホ非対応 (PC 前提).
//
// 位置 / 並び順 / ラベル / 永続化のデータモデルは `layerPopup.ts` (pure helper、
// node vitest 対象) に集約し、本 component はそれを組み合わせて描画するだけ.
//
// S3 スライス1 (floating-panels-2026-08-01.md §8): drag 移動は S2 共有
// `usePanelDragGrip` + `floatShellGeometry` を使う (= ColorWheelPalette と同じ
// 移行。旧実装は `preventDefault()` を呼んでおらずネイティブ drag/選択が漏れる
// 潜在バグを抱えていた。挙動不変: clamp margin は旧 `clampPopupPosition` の
// 既定 8px をそのまま渡す)。

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RegionLayerRoles } from '../../../application/regionMoveFlow.js';
import {
  clampFloatPosition,
  type FloatPoint,
  floatPositionFromPointer,
} from '../shared/floatShellGeometry.js';
import { LayerOpacitySlider, shouldSelectOnRowClick } from '../shared/LayerOpacitySlider.js';
import { LayerThumb } from '../shared/LayerThumb.js';
import type { LayerOpacityControl } from '../shared/layerOpacity.js';
import type { LayerThumbnailHub } from '../shared/layerThumbnailHub.js';
import { RegionRoleTag } from '../shared/RegionRoleTag.js';
import { usePanelDragGrip } from '../shared/usePanelDragGrip.js';
import type { SidebarDockSide } from './classicToolFloat.js';
import {
  buildLayerRows,
  defaultLayerPopupPosition,
  type FloatingPopupPosition,
  LAYER_POPUP_NOMINAL_WIDTH,
  readLayerPopupPosition,
  writeLayerPopupPosition,
} from './layerPopup.js';
import { outsetFrame } from './legacyFrames.js';

/** 旧 `clampPopupPosition` の既定 margin (8px) と揃える (= 挙動不変)。 */
const POPUP_EDGE_INSET_PX = 8;

export interface LayerPopupProps {
  /** 範囲を浮かせている間だけ非 null。行に「置き先」「取った元」を添える (選択は変えない)。 */
  readonly regionRoles?: RegionLayerRoles | null;
  /** 現在アクティブな層 id (= `PenConfig.layerId`). 行の選択状態の source-of-truth. */
  readonly activeLayerId: number;
  /**
   * 各層の現在の表示状態 (= 👁 トグル結果). 2026-05-31 layer-popup-visibility-toggle
   * (`w3-classic-layers.md §2.2.1`): LayerPopup を hide → 再表示するシナリオで
   * 表示状態を保持するため、popup ローカルではなく caller (= ClassicLayout) で
   * state を持つ. 未指定 layer は visible 扱い (旧 `initialLayerVisibility` 初期値と整合).
   */
  readonly visibility: Record<number, boolean>;
  /** 行クリック / ラジオ選択でアクティブ層を切替える. caller が PenConfig.layerId を更新. */
  readonly onSelectActive: (layerId: number) => void;
  /**
   * 👁 トグル. caller が `engine.setLayerVisible(layerId, visible)` を呼ぶ
   * (= クライアントローカル表示トグル, broadcast しない). 同経路で `visibility`
   * state も flip する責務.
   */
  readonly onToggleVisible: (layerId: number, visible: boolean) => void;
  /**
   * canvas 背景 (= 下地色) の表示状態 (2026-06-20 canvas-background-color §5/B).
   * 背景は **layer ではない独立 property** なので `visibility` (layer 用 map) と
   * 分離して受ける. 👁 と同じローカル個人ビュー (broadcast しない).
   */
  readonly backgroundVisible: boolean;
  /**
   * 背景の 👁 トグル. caller (共有コントローラ) が React state を切り替えるだけ
   * (2026-06-20 CSS 方式: 出し分けは DrawingCanvas の CSS。engine は触らない).
   * per-layer の `onToggleVisible` と並走する背景専用配線, broadcast しない.
   */
  readonly onToggleBackgroundVisible: (visible: boolean) => void;
  /**
   * 現在の canvas 背景 *色* (rgb24). A-4 ■ swatch (canvas-background-color §4.2).
   * ■ chip の表示色 + (オーナーが開いた) ColorWheelPalette の seed 値.
   */
  readonly backgroundRgb: number;
  /**
   * 背景色変更の権限 (canvas-background-color §4.2). `hasPerm(Perm.CLEAR)` 単独
   * (= room.clearOpen 非依存). true でのみ ■ が押せる. false は ■ グレーアウト +
   * tooltip (全消しボタンの disabled+tooltip 流儀, ただし perm は canSetBackground).
   */
  readonly canSetBackground: boolean;
  /**
   * ■ swatch click (= 背景色 ColorWheelPalette の開閉トグル). open-state +
   * ColorWheelPalette のレンダリングは ClassicLayout 側が所有する (draw 色
   * `colorWheelOpen` と同流儀). 本 popup は pure を保つ (= popup 内に色エディタを
   * 抱えると閉じる callback prop が混入し、source-string smoke test の常駐 popup
   * 不変条件を壊す). `canSetBackground=false` では呼ばれない.
   */
  readonly onToggleBackgroundColorWheel: () => void;
  /**
   * 2026-08-01 追加依頼: Classic サイドバーの現在のドック側 (`classicToolFloat.ts`
   * §12)。既定 `'left'` は従来どおりの右上初期位置。`'right'` のときだけ初期位置を
   * 左上へミラーする (= 右ドック時にサイドバーの常設グリップ帯と重ならないため)。
   * **未指定 (mount 時点で `undefined`) は `'left'` 扱い** (= 呼び出し側を渡し忘れても
   * 既定挙動が変わらない安全側)。既に localStorage に位置が保存されているときは
   * この prop を無視する (= 初期位置の話であって、ユーザーが動かした位置は最優先)。
   */
  readonly dockSide?: SidebarDockSide;
  /**
   * レイヤー表示濃度 (layer-display-opacity-2026-10-12.md)。index = layerId、未指定/範囲外は
   * 100%。自分の画面だけ・永続しない。
   */
  readonly opacities?: readonly number[];
  /** うすさスライダの操作窓口 (engine 未準備は null = 操作しても何も起きない)。 */
  readonly opacityControl?: LayerOpacityControl | null;
  /**
   * レイヤーのサムネ (layer-thumbnails-2026-10-13.md)。番号バッジが届いたサムネに置き換わる。
   * null / 未指定は番号だけ (beta10 と同じ見た目)。
   */
  readonly thumbnails?: LayerThumbnailHub | null;
}

/** rgb24 → CSS hex (PalettePanel / MaskPanel の toCss と同一). */
const rgbToCss = (rgb: number): string => `#${(rgb & 0xffffff).toString(16).padStart(6, '0')}`;

export const LayerPopup = ({
  activeLayerId,
  visibility,
  onSelectActive,
  onToggleVisible,
  backgroundVisible,
  onToggleBackgroundVisible,
  backgroundRgb,
  canSetBackground,
  onToggleBackgroundColorWheel,
  dockSide = 'left',
  opacities = [],
  opacityControl = null,
  thumbnails = null,
  regionRoles = null,
}: LayerPopupProps): React.ReactNode => {
  // 上から id2 → id1 → id0 (ラベルは レイヤー3 / レイヤー2 / レイヤー1). count は
  // CLASSIC_LAYER_COUNT (= 3) を layerPopup.ts が既定にとる (= 3 をハードコードしない).
  const rows = buildLayerRows();

  // 位置 = localStorage 復元 → 無ければ画面右上寄り (描画の邪魔になりにくい隅、
  // ただし dockSide==='right' なら左上へミラー、2026-08-01 追加依頼)。
  // lazy initializer で 1 回だけ算出 (= window 依存を初回 render に閉じ込める)。
  const [pos, setPos] = useState<FloatingPopupPosition>(() => {
    if (typeof window === 'undefined') return defaultLayerPopupPosition(0, 0, dockSide);
    const stored = readLayerPopupPosition(window.localStorage);
    if (stored !== null) return stored;
    return defaultLayerPopupPosition(window.innerWidth, window.innerHeight, dockSide);
  });
  // 2026-08-01 追加依頼、実機検証で発覚した抜け穴の修正: 本 popup は「出しっぱなし」
  // (§2.2、remount しない) なので、mount 時点のミラーだけでは「同一セッション内で
  // 後からサイドバーを右ドックへドラッグする」実際の repro を救えない (= popup は
  // 既にマウント済で dockSide の初期値しか見ていないため)。**ユーザーがまだ動かして
  // いない場合に限り**、dockSide が変わるたびに再ミラーする (= 一度でもドラッグした
  // ら hasUserPositionedRef が立ち、以後 dockSide が変わっても位置を上書きしない=
  // 「動かした位置を最優先」を維持する)。
  const hasUserPositionedRef = useRef<boolean>(
    typeof window !== 'undefined' && readLayerPopupPosition(window.localStorage) !== null,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || hasUserPositionedRef.current) return;
    setPos(defaultLayerPopupPosition(window.innerWidth, window.innerHeight, dockSide));
  }, [dockSide]);
  // root 要素 ref. ドラッグ / resize / 初回 clamp で実寸 (getBoundingClientRect)
  // を読む. 高さは行数で可変なので実測が望ましい (= 幅は w-[208px] 固定だが
  // 統一のため両軸とも ref から取る).
  const panelRef = useRef<HTMLElement | null>(null);

  // 実寸を読む helper. ref 未取得時 (= 初回 render 直後) は nominal 幅 + 概算高さ.
  const measurePanel = useCallback((): { width: number; height: number } => {
    const el = panelRef.current;
    if (el !== null) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return { width: r.width, height: r.height };
    }
    // ヘッダ + 3 行ぶんの概算 (= 初期 clamp の fallback、実測できたら上書きされる).
    return { width: LAYER_POPUP_NOMINAL_WIDTH, height: 140 };
  }, []);

  // ドラッグ確定 (pointerup) で位置を localStorage に永続化する (per-user 記憶).
  const persistPosition = useCallback((next: FloatingPopupPosition): void => {
    if (typeof window === 'undefined') return;
    writeLayerPopupPosition(window.localStorage, next);
  }, []);

  // 実測 grab-offset (= 掴んだ点と panel 左上の相対距離、S2 §11 と同じ流儀).
  const grabOffsetRef = useRef<FloatPoint | null>(null);
  const grip = usePanelDragGrip({
    onDragStart: (offset) => {
      grabOffsetRef.current = offset;
    },
    onDragMove: (clientX, clientY) => {
      const offset = grabOffsetRef.current ?? { x: 0, y: 0 };
      setPos(
        clampFloatPosition(
          floatPositionFromPointer(clientX, clientY, offset),
          measurePanel(),
          { width: window.innerWidth, height: window.innerHeight },
          POPUP_EDGE_INSET_PX,
        ),
      );
    },
    onDragEnd: (committed) => {
      grabOffsetRef.current = null;
      // pointerup (= committed) 時点の最新 pos を保存する (旧実装と同じ「確定時のみ
      // 永続化」). Escape/cancel は保存しない.
      if (committed) {
        // 2026-08-01 追加依頼: ここでユーザーが実際に動かしたと確定する。以後は
        // dockSide が変わっても上の再ミラー effect が発火しないようにする。
        hasUserPositionedRef.current = true;
        setPos((cur) => {
          persistPosition(cur);
          return cur;
        });
      }
    },
    measureGrabOffset: (e) => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
  });

  // ─── 初回 clamp: localStorage 復元位置が、保存時より小さい画面ではみ出す
  //     ケースを mount 後 (= panel 実測可) に画面内へ引き戻す. ───────────────
  // measurePanel / persistPosition は useCallback で安定参照なので、deps に
  // 列挙しても再実行されず実質「mount 時 1 回」のまま (biome-ignore 不要).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setPos((cur) => {
      const clamped = clampFloatPosition(
        cur,
        measurePanel(),
        { width: window.innerWidth, height: window.innerHeight },
        POPUP_EDGE_INSET_PX,
      );
      // 引き戻した場合は永続化も更新しておく (= 次回も画面内から始まる).
      if (clamped.x !== cur.x || clamped.y !== cur.y) persistPosition(clamped);
      return clamped;
    });
  }, [measurePanel, persistPosition]);

  // ─── window resize で画面外に取り残されたら引き戻す ──────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = (): void => {
      setPos((cur) => {
        const clamped = clampFloatPosition(
          cur,
          measurePanel(),
          { width: window.innerWidth, height: window.innerHeight },
          POPUP_EDGE_INSET_PX,
        );
        if (clamped.x !== cur.x || clamped.y !== cur.y) persistPosition(clamped);
        return clamped;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measurePanel, persistPosition]);

  // active 層が hide されている = 描いても見えない状態. UX 上の薄い警告に使う
  // (= 過剰実装しない、行に注記 + 枠を点線にする程度).
  const activeHidden = visibility[activeLayerId] === false;

  /**
   * 2026-05-31 layer-popup-visibility-toggle: visibility は ClassicLayout が
   * 単一 source-of-truth として持つ (= popup hide → 再表示でも保持). 本 popup は
   * 現在値を読み取って `onToggleVisible(id, !current)` を呼ぶだけ.
   * StrictMode 二重実行下でも caller 側の reducer 等で冪等性は担保される前提.
   */
  const handleToggleVisible = useCallback(
    (id: number): void => {
      const currentVisible = visibility[id] !== false;
      onToggleVisible(id, !currentVisible);
    },
    [visibility, onToggleVisible],
  );

  return (
    // 常駐 floating panel. <section aria-label> で「レイヤー」領域を意味付け
    // (= role="group" の代わりに semantic element を使い biome useSemanticElements
    // 警告を回避). position: fixed + 右上寄り. z-40 は chat detach 小窓と同列
    // (= 描画 canvas より前、modal より後ろ). Classic 青基調トークンに合わせる
    // (--color-panel / --color-bg-soft / --color-border).
    <section
      ref={panelRef}
      data-testid="classic-layer-popup"
      aria-label="レイヤー"
      className={`fixed z-40 flex w-[208px] flex-col bg-[var(--color-panel)] text-white select-none ${outsetFrame}`}
      style={{ left: pos.x, top: pos.y }}
    >
      {/* タイトルバー = drag handle. 閉じるボタンは無い (常駐).
          ペンタブ対策:
          - `touchAction: 'none'` で OS の pan / scroll ジェスチャ解釈を抑止
            (= 解釈されると pointercancel が飛んできて drag が即終了する).
          - `setPointerCapture` で pen のジッタでハンドル外に出ても追従させる
            (PreviewOverlay / ColorWheelPalette / ChatPanel 発言小窓と同流儀). */}
      <div
        data-testid="classic-layer-popup-header"
        className="flex shrink-0 cursor-move items-center justify-between border-b border-black bg-[var(--color-bg-soft)] px-2 py-1 text-[10px] font-bold"
        style={{ touchAction: 'none' }}
        onPointerDown={grip.onPointerDown}
        onPointerMove={grip.onPointerMove}
        onPointerUp={grip.onPointerUp}
        onPointerCancel={grip.onPointerCancel}
        onLostPointerCapture={grip.onLostPointerCapture}
      >
        <span>レイヤー</span>
      </div>

      {/* 3 行リスト (上から レイヤー3 → レイヤー2 → レイヤー1). */}
      <ul className="flex flex-col">
        {rows.map((row) => {
          const isActive = row.id === activeLayerId;
          const isVisible = visibility[row.id] !== false;
          return (
            // biome-ignore lint/a11y/useKeyWithClickEvents: 行クリックはマウス/タッチ用の拡張。キーボードは行内の選択ボタン (aria-pressed) が担う
            <li
              key={row.id}
              // active 行は薄背景 + 左に印. active かつ hidden は点線枠で薄く警告
              // (= 描けるが見えない状態の最小サイン、§タスク4 過剰実装しない).
              data-testid={`classic-layer-popup-row-${row.id}`}
              onClick={(e) => {
                if (shouldSelectOnRowClick(e)) onSelectActive(row.id);
              }}
              className={`relative flex cursor-pointer items-center gap-1 border-b border-[var(--color-border)] px-1 text-[11px] ${
                isActive
                  ? 'bg-[var(--color-bg-soft)] shadow-[inset_3px_0_0_var(--color-accent)]'
                  : 'hover:bg-[var(--color-bg-soft)]/50'
              } ${isActive && !isVisible ? 'border-dashed opacity-70' : ''}`}
            >
              {/* 👁 表示トグル (クライアントローカル). */}
              <button
                type="button"
                aria-label={`${row.label}レイヤーの表示`}
                aria-pressed={isVisible}
                title={isVisible ? '表示中 (クリックで非表示)' : '非表示 (クリックで表示)'}
                onClick={() => handleToggleVisible(row.id)}
                className={`flex h-5 w-5 shrink-0 items-center justify-center text-[12px] leading-none ${
                  isVisible ? 'opacity-100' : 'opacity-30'
                }`}
              >
                {isVisible ? '👁' : '🚫'}
              </button>

              {/* 番号バッジ (上から 3・2・1 = 重なり順)。名前は 3 層では不要。
                  キーボード用の選択ボタンを兼ねる (行全体も選択対象。つまみ・👁 は除く)。
                  2026-10-12: 行末の ●○ は行全体選択で冗長になったので廃止し、その幅は
                  将来この番号をサムネにする分に回す (layer-enhancements-2026-10-11.md 追記)。 */}
              <LayerThumb
                layerId={row.id}
                hub={thumbnails}
                active={isActive}
                ariaLabel={`${row.label}レイヤーを選択`}
                dataManual="layer.select"
                testId={`classic-layer-popup-badge-${row.id}`}
                onSelect={() => onSelectActive(row.id)}
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-[10px] font-bold ${
                  isActive
                    ? 'bg-[var(--color-accent)] text-black'
                    : 'bg-[var(--color-bg-inset)] hover:bg-[var(--color-bg-soft)]'
                }`}
              >
                {row.id + 1}
              </LayerThumb>

              {/* 常設うすさスライダ (自分の画面だけ)。つまみを掴んでいる間だけ動く。 */}
              <LayerOpacitySlider
                layerId={row.id}
                label={row.label}
                opacity={opacities[row.id] ?? 1}
                control={opacityControl}
                dimmedLook={!isVisible}
                testId={`classic-layer-popup-opacity-${row.id}`}
              />
              <RegionRoleTag roles={regionRoles} layerId={row.id} />
            </li>
          );
        })}

        {/* 背景 疑似行 (2026-06-20 canvas-background-color §3.2). rows.map() の外 =
            本物 layer 配列に混ぜない (鉄則: 背景は Layer ではない独立 property).
            列の最下段 (compose 上 bg は最下層、列は上=最前面なので背景が一番下).
            「active にならない」を 4 点で伝える:
              (1) 行背景 = --color-bg-inset (active 予約 --color-bg-soft とは別トーン)
              (2) ● ラジオマーカーを出さない
              (3) ラベルは非インタラクティブ (onSelectActive 無し / hover 無し / cursor-default)
              (4) layer 群との境界に一段濃い区切り線 (border-t border-black)
            触れるのは 👁 のみ (■ swatch = 色変更 A は別 PR). */}
        <li
          data-testid="classic-layer-popup-background-row"
          className="flex items-center gap-1 border-t-2 border-black bg-[var(--color-bg-inset)] px-1 py-1 text-[11px]"
        >
          {/* 👁 背景表示トグル (クライアントローカル / broadcast しない). */}
          <button
            type="button"
            aria-label="背景の表示"
            aria-pressed={backgroundVisible}
            title={backgroundVisible ? '表示中 (クリックで非表示)' : '非表示 (クリックで表示)'}
            onClick={() => onToggleBackgroundVisible(!backgroundVisible)}
            className={`flex h-5 w-5 shrink-0 items-center justify-center text-[12px] leading-none ${
              backgroundVisible ? 'opacity-100' : 'opacity-30'
            }`}
          >
            {backgroundVisible ? '👁' : '🚫'}
          </button>

          {/* 非インタラクティブなラベル (= active に選べない). button ではなく span,
              hover ハイライト無し, cursor-default. ラジオマーカーも出さない. */}
          <span className="flex flex-1 cursor-default items-center px-1 py-0.5 font-bold">
            背景
          </span>

          {/* ■ swatch (A-4, canvas-background-color §3.1/§4.2). 現在背景色を常に表示
              (read-only chip 兼). オーナー (canSetBackground) は click で背景用
              ColorWheelPalette を開く. 非オーナーは disabled + グレーアウト + tooltip
              (全消しボタンの disabled+tooltip 流儀, perm は canSetBackground). */}
          <button
            type="button"
            data-testid="classic-layer-popup-background-swatch"
            aria-label={
              canSetBackground
                ? `背景色 ${rgbToCss(backgroundRgb)} (クリックで変更)`
                : '背景色 (変更には権限が必要)'
            }
            title={canSetBackground ? '背景色を変更' : '背景色変更の権限がありません'}
            disabled={!canSetBackground}
            onClick={() => {
              if (!canSetBackground) return;
              onToggleBackgroundColorWheel();
            }}
            className={`h-5 w-5 shrink-0 border border-black ${
              canSetBackground ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'
            }`}
            style={{ backgroundColor: rgbToCss(backgroundRgb) }}
          />
        </li>
      </ul>

      {/* active 層を hide したときの薄い注記 (= 描けるが見えない、§タスク4). */}
      {activeHidden && (
        <p className="px-1 py-0.5 text-[9px] text-[var(--color-text-muted)]">
          選択中の層は非表示です
        </p>
      )}
    </section>
  );
};
